import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { Download, RefreshCw, WifiOff, X } from 'lucide-react';
import { isInTelegram } from '@/lib/telegram';
import { useAuth } from '@/hooks/use-auth';
import { t } from '@/lib/i18n';
import {
  appUpdateCoordinator,
  type AppUpdateCoordinator,
} from '@/lib/app-update-coordinator';

// Chrome fires `beforeinstallprompt` when the PWA is installable. The event is
// not in the standard TS DOM lib, so type the slice we use.
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const INSTALL_DISMISS_KEY = 'gn:pwa-install-dismissed';
const UPDATE_CHECK_INTERVAL_MS = 5 * 60 * 1000;
const UPDATE_SNOOZE_MS = 5 * 60 * 1000;
const UPDATE_ACTIVATION_TIMEOUT_MS = 12 * 1000;
const MAX_UPDATE_DEFERS = 3;

function BrowserPwaPrompt({
  allowInstall,
  coordinator,
}: {
  allowInstall: boolean;
  coordinator: AppUpdateCoordinator;
}) {
  const activationTimerRef = useRef<number | null>(null);
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    immediate: true,
    onNeedReload() {
      if (activationTimerRef.current !== null) {
        window.clearTimeout(activationTimerRef.current);
        activationTimerRef.current = null;
      }
      void coordinator.handleControllerChange().then((result) => {
        if (result.status !== 'activating') setApplyingUpdate(false);
      });
    },
    onRegistered(nextRegistration) {
      setRegistration(nextRegistration ?? null);
    },
  });

  const [installEvt, setInstallEvt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installDismissed, setInstallDismissed] = useState<boolean>(() => {
    try { return localStorage.getItem(INSTALL_DISMISS_KEY) === '1'; } catch { return false; }
  });
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [snoozedUntil, setSnoozedUntil] = useState<number | null>(null);
  const [deferCount, setDeferCount] = useState(0);
  const [applyingUpdate, setApplyingUpdate] = useState(false);
  const blockedReason = useSyncExternalStore(
    coordinator.subscribe,
    coordinator.getBlockedReason,
    coordinator.getBlockedReason,
  );

  useEffect(() => {
    coordinator.setWaitingActivator(
      needRefresh ? () => updateServiceWorker(true) : null,
    );
    return () => coordinator.setWaitingActivator(null);
  }, [coordinator, needRefresh, updateServiceWorker]);

  useEffect(() => {
    if (!registration) return;
    const checkForUpdate = () => registration.update().catch(() => undefined);
    void checkForUpdate();
    const timer = window.setInterval(() => void checkForUpdate(), UPDATE_CHECK_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [registration]);

  useEffect(() => () => {
    if (activationTimerRef.current !== null) {
      window.clearTimeout(activationTimerRef.current);
    }
  }, []);

  useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  useEffect(() => {
    if (!needRefresh) {
      setSnoozedUntil(null);
      setDeferCount(0);
      setApplyingUpdate(false);
      if (activationTimerRef.current !== null) {
        window.clearTimeout(activationTimerRef.current);
        activationTimerRef.current = null;
      }
      return;
    }
    if (snoozedUntil === null) return;

    const revealIfDue = () => {
      if (Date.now() >= snoozedUntil) setSnoozedUntil(null);
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') revealIfDue();
    };
    const timer = window.setTimeout(revealIfDue, Math.max(0, snoozedUntil - Date.now()));
    window.addEventListener('focus', revealIfDue);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('focus', revealIfDue);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [needRefresh, snoozedUntil]);

  useEffect(() => {
    const onPrompt = (e: Event) => {
      // Suppress the default mini-infobar; surface our own banner instead.
      e.preventDefault();
      setInstallEvt(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => setInstallEvt(null);
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const { isAuthed } = useAuth();

  const showOffline = !isOnline;
  const hasBlockedTakeover = !!blockedReason && coordinator.hasWaitingUpdate();
  const showUpdate = !showOffline
    && (needRefresh || hasBlockedTakeover)
    && (snoozedUntil === null || !!blockedReason);
  const showInstall = allowInstall
    && !showOffline
    && !showUpdate
    && isAuthed
    && !!installEvt
    && !installDismissed;
  const updateBody = blockedReason
    ?? (deferCount >= MAX_UPDATE_DEFERS ? t('pwa.update.waiting_body') : t('pwa.update.body'));
  if (!showOffline && !showUpdate && !showInstall) return null;

  const onInstall = async () => {
    if (!installEvt) return;
    await installEvt.prompt();
    await installEvt.userChoice.catch(() => undefined);
    setInstallEvt(null);
  };

  const dismissInstall = () => {
    setInstallDismissed(true);
    try { localStorage.setItem(INSTALL_DISMISS_KEY, '1'); } catch { /* storage unavailable */ }
  };

  const deferUpdate = () => {
    coordinator.clearBlockedReason();
    const nextCount = deferCount + 1;
    setDeferCount(nextCount);
    setSnoozedUntil(nextCount < MAX_UPDATE_DEFERS ? Date.now() + UPDATE_SNOOZE_MS : null);
  };

  const applyUpdate = async () => {
    if (applyingUpdate) return;
    if (activationTimerRef.current !== null) {
      window.clearTimeout(activationTimerRef.current);
      activationTimerRef.current = null;
    }
    coordinator.clearBlockedReason();
    setApplyingUpdate(true);
    const result = await coordinator.activate(() => updateServiceWorker(true));
    if (result.status !== 'activating') {
      setApplyingUpdate(false);
      return;
    }
    activationTimerRef.current = window.setTimeout(() => {
      activationTimerRef.current = null;
      coordinator.allowActivationRetry();
      setApplyingUpdate(false);
    }, UPDATE_ACTIVATION_TIMEOUT_MS);
  };

  return (
    <div className="pwa-prompt" role="status" aria-live="polite">
      <div className="pwa-prompt-card">
        <span className="pwa-prompt-ic" aria-hidden>
          {showOffline ? <WifiOff /> : showUpdate ? <RefreshCw /> : <Download />}
        </span>
        <div className="pwa-prompt-text">
          <div className="pwa-prompt-title">
            {showOffline ? t('pwa.offline.title') : showUpdate ? t('pwa.update.title') : t('pwa.install.title')}
          </div>
          <div className="pwa-prompt-body">
            {showOffline ? t('pwa.offline.body') : showUpdate ? updateBody : t('pwa.install.body')}
          </div>
        </div>
        {showUpdate && (
          <button
            type="button"
            className="btn primary pwa-prompt-action"
            onClick={applyUpdate}
            disabled={applyingUpdate}
          >
            {t('pwa.update.action')}
          </button>
        )}
        {showInstall && (
          <>
            <button type="button" className="btn primary pwa-prompt-action" onClick={onInstall}>
              {t('pwa.install.action')}
            </button>
            <button
              type="button"
              className="icon-btn pwa-prompt-close"
              onClick={dismissInstall}
              aria-label={t('pwa.dismiss')}
            >
              <X size={16} />
            </button>
          </>
        )}
        {showUpdate && deferCount < MAX_UPDATE_DEFERS && !applyingUpdate && (
          <button
            type="button"
            className="icon-btn pwa-prompt-close"
            onClick={deferUpdate}
            aria-label={t('pwa.update.later')}
          >
            <X size={16} />
          </button>
        )}
      </div>
    </div>
  );
}

export function PwaPrompt({
  coordinator = appUpdateCoordinator,
}: {
  coordinator?: AppUpdateCoordinator;
} = {}) {
  return <BrowserPwaPrompt allowInstall={!isInTelegram()} coordinator={coordinator} />;
}
